import L from "leaflet";

const STATE_KEY = "hydrotools:slip-inventory:v2";
const API_BASE = "/api/slip-inventory/v1/slip-inventory";
const RECORD_HEADERS = [
  "measurement_id",
  "marina_id",
  "marina_name",
  "slip_id",
  "vendor_slip_ref",
  "measured_linear_feet",
  "measured_width_feet",
  "measured_depth_feet",
  "measurement_date",
  "geometry_wkt",
  "confidence_score",
  "record_created_timestamp",
];

function esc(v) {
  return String(v ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll("\"", "&quot;");
}

function todayDate() {
  return new Date().toISOString().slice(0, 10);
}

function defaultState() {
  return {
    marinaId: "",
    marinaName: "",
    measurementDate: todayDate(),
    confidenceScore: "High",
    selectedMeasurementId: "",
    slipIdPart1: "",
    slipIdPart2: "",
    slipIdPart3: "",
    slipIdAutoIncrement: true,
    slipIdIncrementBy: 1,
    lineSlipCount: 6,
  };
}

function readState() {
  try {
    const raw = localStorage.getItem(STATE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    const merged = { ...defaultState(), ...(parsed && typeof parsed === "object" ? parsed : {}) };
    merged.measurementDate = todayDate();
    merged.slipIdIncrementBy = Math.max(1, Number(merged.slipIdIncrementBy || 1) || 1);
    merged.slipIdAutoIncrement = merged.slipIdAutoIncrement !== false;
    merged.lineSlipCount = Math.max(2, Number(merged.lineSlipCount || 6) || 6);
    return merged;
  } catch {
    return defaultState();
  }
}

function persistState(state) {
  try {
    localStorage.setItem(STATE_KEY, JSON.stringify(state));
  } catch {}
}

function makeId(prefix = "MEAS") {
  return `${prefix}-${Math.random().toString(36).slice(2, 10).toUpperCase()}`;
}

function buildSlipIdFromState(state) {
  return [
    String(state?.slipIdPart1 || ""),
    String(state?.slipIdPart2 || ""),
    String(state?.slipIdPart3 || ""),
  ].filter((part) => part !== "").join("");
}

function incrementSlipIdNumber(state) {
  if (state?.slipIdAutoIncrement !== true) return;
  const current = String(state?.slipIdPart3 || "").trim();
  const incrementBy = Math.max(1, Number(state?.slipIdIncrementBy || 1) || 1);
  if (!/^-?\d+$/.test(current)) return;
  state.slipIdPart3 = String(Number(current) + incrementBy);
}

function previewSlipIds(state, count) {
  const total = Math.max(1, Number(count || 0) || 0);
  const base = {
    slipIdPart1: String(state?.slipIdPart1 || ""),
    slipIdPart2: String(state?.slipIdPart2 || ""),
    slipIdPart3: String(state?.slipIdPart3 || ""),
    slipIdAutoIncrement: state?.slipIdAutoIncrement !== false,
    slipIdIncrementBy: Math.max(1, Number(state?.slipIdIncrementBy || 1) || 1),
  };
  const values = [];
  for (let index = 0; index < total; index += 1) {
    values.push(buildSlipIdFromState(base) || `SLIP-${String(index + 1).padStart(4, "0")}`);
    incrementSlipIdNumber(base);
  }
  return values;
}

function buildPointWkt(lng, lat) {
  return `POINT(${Number(lng).toFixed(6)} ${Number(lat).toFixed(6)})`;
}

function parsePointWkt(wkt) {
  const match = /^POINT\s*\(\s*(-?\d+(?:\.\d+)?)\s+(-?\d+(?:\.\d+)?)\s*\)$/i.exec(String(wkt || "").trim());
  if (!match) return null;
  const lng = Number(match[1]);
  const lat = Number(match[2]);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
}

function clampNumber(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function movingAverage(values, radius = 2) {
  const source = Array.isArray(values) ? values : [];
  if (!source.length || radius <= 0) return source.slice();
  return source.map((_, index) => {
    let total = 0;
    let count = 0;
    const start = Math.max(0, index - radius);
    const end = Math.min(source.length - 1, index + radius);
    for (let cursor = start; cursor <= end; cursor += 1) {
      total += Number(source[cursor]) || 0;
      count += 1;
    }
    return count ? total / count : 0;
  });
}

function seriesStats(values) {
  const source = Array.isArray(values) ? values : [];
  if (!source.length) return { mean: 0, stdDev: 0 };
  const mean = source.reduce((sum, value) => sum + (Number(value) || 0), 0) / source.length;
  const variance = source.reduce((sum, value) => {
    const delta = (Number(value) || 0) - mean;
    return sum + (delta * delta);
  }, 0) / source.length;
  return { mean, stdDev: Math.sqrt(Math.max(0, variance)) };
}

function medianValue(values) {
  const nums = (Array.isArray(values) ? values : [])
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value))
    .sort((a, b) => a - b);
  if (!nums.length) return null;
  const mid = Math.floor(nums.length / 2);
  return nums.length % 2 ? nums[mid] : ((nums[mid - 1] + nums[mid]) / 2);
}

function detectSignalPeaks(signal, { minDistance = 6, minProminence = 0 } = {}) {
  const values = Array.isArray(signal) ? signal : [];
  const distance = Math.max(1, Math.round(Number(minDistance) || 1));
  const prominenceFloor = Math.max(0, Number(minProminence) || 0);
  const candidates = [];
  for (let index = 1; index < values.length - 1; index += 1) {
    const current = Number(values[index]) || 0;
    const prev = Number(values[index - 1]) || 0;
    const next = Number(values[index + 1]) || 0;
    if (!(current > prev && current >= next)) continue;
    const leftBase = Math.min(prev, Number(values[Math.max(0, index - distance)]) || prev);
    const rightBase = Math.min(next, Number(values[Math.min(values.length - 1, index + distance)]) || next);
    const prominence = current - Math.max(leftBase, rightBase);
    if (prominence < prominenceFloor) continue;
    candidates.push({ index, value: current, prominence });
  }
  candidates.sort((a, b) => b.prominence - a.prominence);
  const selected = [];
  for (const candidate of candidates) {
    if (selected.some((entry) => Math.abs(entry.index - candidate.index) < distance)) continue;
    selected.push(candidate);
  }
  return selected.sort((a, b) => a.index - b.index);
}

function sampleCanvasLuma(imageData, width, height, x, y) {
  const px = clampNumber(Math.round(Number(x) || 0), 0, Math.max(0, width - 1));
  const py = clampNumber(Math.round(Number(y) || 0), 0, Math.max(0, height - 1));
  const offset = ((py * width) + px) * 4;
  const r = Number(imageData[offset] || 0);
  const g = Number(imageData[offset + 1] || 0);
  const b = Number(imageData[offset + 2] || 0);
  return (0.2126 * r) + (0.7152 * g) + (0.0722 * b);
}

function buildImagerySpacingSignal(imageData, width, height, startPoint, endPoint, { sampleCount, crossSampleCount, corridorHalfWidthPx }) {
  const dx = Number(endPoint.x) - Number(startPoint.x);
  const dy = Number(endPoint.y) - Number(startPoint.y);
  const lineLengthPx = Math.hypot(dx, dy);
  if (!Number.isFinite(lineLengthPx) || lineLengthPx <= 0) return [];
  const tangentX = dx / lineLengthPx;
  const tangentY = dy / lineLengthPx;
  const normalX = -tangentY;
  const normalY = tangentX;
  const crossCount = Math.max(5, Number(crossSampleCount) || 13);
  const halfWidth = Math.max(4, Number(corridorHalfWidthPx) || 16);
  const values = [];
  for (let index = 0; index < sampleCount; index += 1) {
    const ratio = sampleCount <= 1 ? 0 : (index / (sampleCount - 1));
    const centerX = Number(startPoint.x) + (dx * ratio);
    const centerY = Number(startPoint.y) + (dy * ratio);
    let edgeEnergy = 0;
    let previous = null;
    for (let crossIndex = 0; crossIndex < crossCount; crossIndex += 1) {
      const crossRatio = crossCount <= 1 ? 0 : (crossIndex / (crossCount - 1));
      const offset = ((crossRatio * 2) - 1) * halfWidth;
      const sampleX = centerX + (normalX * offset);
      const sampleY = centerY + (normalY * offset);
      const luma = sampleCanvasLuma(imageData, width, height, sampleX, sampleY);
      if (previous != null) edgeEnergy += Math.abs(luma - previous);
      previous = luma;
    }
    values.push(edgeEnergy);
  }
  return movingAverage(values, 2);
}

function estimateSignalLag(signal, { minLag, maxLag }) {
  const source = Array.isArray(signal) ? signal : [];
  if (source.length < 8) return null;
  const { mean, stdDev } = seriesStats(source);
  if (!Number.isFinite(stdDev) || stdDev < 1e-6) return null;
  const normalized = source.map((value) => ((Number(value) || 0) - mean) / stdDev);
  const startLag = clampNumber(Math.round(Number(minLag) || 0), 1, Math.max(1, source.length - 2));
  const endLag = clampNumber(Math.round(Number(maxLag) || 0), startLag, Math.max(startLag, source.length - 2));
  let best = null;
  const scores = [];
  for (let lag = startLag; lag <= endLag; lag += 1) {
    let total = 0;
    let count = 0;
    for (let index = 0; index + lag < normalized.length; index += 1) {
      total += normalized[index] * normalized[index + lag];
      count += 1;
    }
    if (!count) continue;
    const score = total / count;
    scores.push(score);
    if (!best || score > best.score) best = { lag, score };
  }
  if (!best) return null;
  const sortedScores = scores.slice().sort((a, b) => b - a);
  const secondBest = sortedScores[1] ?? sortedScores[0] ?? 0;
  const cycles = source.length / Math.max(1, best.lag);
  const sharpness = Math.max(0, best.score - secondBest);
  const confidence = clampNumber(((best.score - 0.15) / 0.85), 0, 1)
    * clampNumber((cycles - 2) / 5, 0, 1)
    * clampNumber((sharpness + 0.04) / 0.22, 0, 1);
  return {
    lag: best.lag,
    score: best.score,
    confidence,
  };
}

function estimatePitchFromSignal(signal, { minLag, maxLag }) {
  const values = Array.isArray(signal) ? signal : [];
  if (values.length < 8) return null;
  const stats = seriesStats(values);
  const smoothed = movingAverage(values, 1);
  const peaks = detectSignalPeaks(smoothed, {
    minDistance: Math.max(3, Math.round((Number(minLag) || 6) * 0.65)),
    minProminence: Math.max(0.5, stats.stdDev * 0.22),
  });
  const peakIntervals = [];
  for (let index = 1; index < peaks.length; index += 1) {
    const delta = peaks[index].index - peaks[index - 1].index;
    if (delta >= minLag * 0.5 && delta <= maxLag * 1.5) peakIntervals.push(delta);
  }
  const peakMedian = medianValue(peakIntervals);
  const lagEstimate = estimateSignalLag(smoothed, { minLag, maxLag });
  const autocorrLag = lagEstimate?.lag || null;
  let chosenLag = null;
  let method = "";
  if (peakMedian && autocorrLag) {
    const ratio = autocorrLag / Math.max(1e-6, peakMedian);
    if (ratio > 1.75 && ratio < 2.25) {
      chosenLag = peakMedian;
      method = "peaks";
    } else if (ratio > 0.45 && ratio < 0.75) {
      chosenLag = autocorrLag;
      method = "autocorr";
    } else {
      chosenLag = Math.abs(peakMedian - autocorrLag) <= (minLag * 0.45) ? ((peakMedian + autocorrLag) / 2) : peakMedian;
      method = "hybrid";
    }
  } else if (peakMedian) {
    chosenLag = peakMedian;
    method = "peaks";
  } else if (autocorrLag) {
    chosenLag = autocorrLag;
    method = "autocorr";
  } else {
    return null;
  }
  chosenLag = clampNumber(chosenLag, minLag, maxLag);
  const intervalStats = seriesStats(peakIntervals);
  const peakConsistency = peakIntervals.length >= 2 && peakMedian
    ? clampNumber(1 - ((intervalStats.stdDev || 0) / Math.max(peakMedian, 1)), 0, 1)
    : 0;
  const confidence = clampNumber(Math.max(lagEstimate?.confidence || 0, peakConsistency * 0.9), 0, 1);
  return {
    lag: chosenLag,
    confidence,
    method,
    peakCount: peaks.length,
  };
}

function chooseConsensusPitchEstimate(estimates = []) {
  const candidates = (Array.isArray(estimates) ? estimates : [])
    .filter((entry) => entry && Number.isFinite(entry.lag) && entry.lag > 0 && Number.isFinite(entry.confidence));
  if (!candidates.length) return null;
  const toleranceFor = (lag) => Math.max(3, Number(lag) * 0.18);
  let bestGroup = null;
  candidates.forEach((candidate) => {
    const group = candidates.filter((entry) => Math.abs(entry.lag - candidate.lag) <= toleranceFor(candidate.lag));
    const support = group.reduce((sum, entry) => sum + Math.max(0.05, Number(entry.confidence) || 0), 0);
    const weightedLag = group.reduce((sum, entry) => sum + (entry.lag * Math.max(0.05, Number(entry.confidence) || 0)), 0) / Math.max(1e-6, support);
    if (!bestGroup || support > bestGroup.support) {
      bestGroup = {
        support,
        weightedLag,
        members: group,
      };
    }
  });
  if (!bestGroup) return candidates[0];
  const bestConfidence = clampNumber(bestGroup.support / Math.max(1, bestGroup.members.length), 0, 1);
  const methods = Array.from(new Set(bestGroup.members.map((entry) => entry.method).filter(Boolean)));
  return {
    lag: bestGroup.weightedLag,
    confidence: bestConfidence,
    method: methods.join("+") || "consensus",
    samples: bestGroup.members.length,
  };
}

function buildSparklinePath(values, width, height, padding = 10) {
  const source = Array.isArray(values) ? values : [];
  if (!source.length) return "";
  const minValue = Math.min(...source);
  const maxValue = Math.max(...source);
  const range = Math.max(1e-6, maxValue - minValue);
  return source.map((value, index) => {
    const x = padding + ((index / Math.max(1, source.length - 1)) * (width - (padding * 2)));
    const y = height - padding - (((value - minValue) / range) * (height - (padding * 2)));
    return `${index === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
}

function normalizeRecord(record) {
  const source = record && typeof record === "object" ? record : {};
  const rawMeasurementDate = String(source.measurement_date || "").trim();
  const measurementDate = rawMeasurementDate.includes("T")
    ? rawMeasurementDate.slice(0, 10)
    : rawMeasurementDate;
  return {
    measurement_id: String(source.measurement_id || ""),
    marina_id: String(source.marina_id || ""),
    marina_name: String(source.marina_name || ""),
    slip_id: String(source.slip_id || ""),
    vendor_slip_ref: String(source.vendor_slip_ref || ""),
    measured_linear_feet: source.measured_linear_feet == null || source.measured_linear_feet === "" ? null : Number(source.measured_linear_feet),
    measured_width_feet: source.measured_width_feet == null || source.measured_width_feet === "" ? null : Number(source.measured_width_feet),
    measured_depth_feet: source.measured_depth_feet == null || source.measured_depth_feet === "" ? null : Number(source.measured_depth_feet),
    measurement_date: measurementDate,
    geometry_wkt: String(source.geometry_wkt || ""),
    confidence_score: String(source.confidence_score || "High"),
    record_created_timestamp: String(source.record_created_timestamp || ""),
  };
}

function downloadText(filename, text, type = "text/plain") {
  const blob = new Blob([String(text || "")], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

function buildCsv(records) {
  const escapeCsv = (value) => {
    const text = value == null ? "" : String(value);
    return /[",\n]/.test(text) ? `"${text.replaceAll("\"", "\"\"")}"` : text;
  };
  const rows = records.map((record) => RECORD_HEADERS.map((header) => escapeCsv(record[header])).join(","));
  return `${RECORD_HEADERS.join(",")}\n${rows.join("\n")}`;
}

async function requestJson(url, options = {}) {
  const response = await fetch(url, {
    method: options.method || "GET",
    headers: {
      "Content-Type": "application/json",
      ...(options.headers || {}),
    },
    body: options.body == null ? undefined : JSON.stringify(options.body),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(String(payload?.error || `Request failed (${response.status})`));
  return payload;
}

export function createSlipInventoryRuntime(app) {
  const map = app.map.instance;
  const root = L.featureGroup().addTo(map);
  const markersLayer = L.featureGroup().addTo(root);
  const previewLayer = L.featureGroup().addTo(root);
  let html2canvasLoader = null;
  let containerEl = null;
  let state = readState();
  let records = [];
  let status = "Slip inventory is ready.";
  let imageryEstimateStatus = "";
  let imageryEstimateDebug = null;
  let pointDropActive = false;
  let pointDropOff = null;
  let lineLayoutActive = false;
  let lineLayoutOff = null;
  let marinaCatalog = [];
  let lineEndpoints = [];
  let lineLayoutMode = "endpoint";
  let lineHoverPoint = null;

  function persist() {
    persistState(state);
  }

  function normalizeMarinaCatalog(items = []) {
    const seen = new Set();
    return (Array.isArray(items) ? items : [])
      .map((item) => {
        const marinaId = String(item?.portfolio?.code || item?.name || "").trim();
        const marinaName = String(item?.name || item?.portfolio?.name || "").trim();
        const lat = Number(item?.lat);
        const lng = Number(item?.lng);
        if (!marinaId || !marinaName || !Number.isFinite(lat) || !Number.isFinite(lng)) return null;
        const key = marinaId.toLowerCase();
        if (seen.has(key)) return null;
        seen.add(key);
        return { marinaId, marinaName, lat, lng };
      })
      .filter(Boolean)
      .sort((a, b) => a.marinaId.localeCompare(b.marinaId));
  }

  function marinaOptionForId(marinaId, fallbackName = "") {
    const text = String(marinaId || "").trim();
    if (!text) return null;
    const existing = marinaCatalog.find((entry) => entry.marinaId === text);
    if (existing) return existing;
    return {
      marinaId: text,
      marinaName: String(fallbackName || text),
      lat: null,
      lng: null,
    };
  }

  function setMarinaDefaultsFromId(marinaId) {
    const marina = marinaOptionForId(marinaId, state.marinaName);
    state.marinaId = marina?.marinaId || "";
    state.marinaName = marina?.marinaName || "";
    persist();
  }

  function selectedRecord() {
    return records.find((record) => record.measurement_id === state.selectedMeasurementId) || null;
  }

  function selectedIndex() {
    const index = records.findIndex((record) => record.measurement_id === state.selectedMeasurementId);
    return index >= 0 ? index : records.length;
  }

  function fitSelection() {
    const current = selectedRecord();
    const point = parsePointWkt(current?.geometry_wkt);
    if (!point) return;
    map.setView([point.lat, point.lng], Math.max(18, Number(map.getZoom?.() || 18)));
  }

  function fitMarinaById(marinaId, fallbackName = "") {
    const marina = marinaOptionForId(marinaId, fallbackName);
    if (!marina || !Number.isFinite(marina.lat) || !Number.isFinite(marina.lng)) return false;
    map.setView([marina.lat, marina.lng], Math.max(16, Number(map.getZoom?.() || 16)));
    return true;
  }

  function interpolateLatLng(start, end, ratio) {
    return {
      lat: Number(start.lat) + ((Number(end.lat) - Number(start.lat)) * ratio),
      lng: Number(start.lng) + ((Number(end.lng) - Number(start.lng)) * ratio),
    };
  }

  function spacingRunPreviewPoints() {
    if (lineEndpoints.length < 2) return [];
    const first = lineEndpoints[0];
    const second = lineEndpoints[1];
    const target = lineEndpoints[2] || lineHoverPoint || second;
    const startPoint = map.latLngToLayerPoint([first.lat, first.lng]);
    const spacingPoint = map.latLngToLayerPoint([second.lat, second.lng]);
    const targetPoint = map.latLngToLayerPoint([target.lat, target.lng]);
    const vx = Number(spacingPoint.x) - Number(startPoint.x);
    const vy = Number(spacingPoint.y) - Number(startPoint.y);
    const spacingPx = Math.hypot(vx, vy);
    if (!Number.isFinite(spacingPx) || spacingPx <= 0) return [first, second];
    const ux = vx / spacingPx;
    const uy = vy / spacingPx;
    const tx = Number(targetPoint.x) - Number(startPoint.x);
    const ty = Number(targetPoint.y) - Number(startPoint.y);
    const projectedLength = Math.max(spacingPx, (tx * ux) + (ty * uy));
    const count = Math.max(2, Math.floor((projectedLength / spacingPx) + 0.35) + 1);
    return Array.from({ length: count }, (_, index) => {
      const point = L.point(startPoint.x + (ux * spacingPx * index), startPoint.y + (uy * spacingPx * index));
      const latlng = map.layerPointToLatLng(point);
      return { lat: Number(latlng.lat), lng: Number(latlng.lng) };
    });
  }

  function linePreviewRatios() {
    const count = Math.max(2, Number(state.lineSlipCount || 0) || 0);
    return Array.from({ length: count }, (_, index) => count <= 1 ? 0 : (index / Math.max(1, count - 1)));
  }

  function linePreviewPoints() {
    if (lineLayoutMode === "spacing") return spacingRunPreviewPoints();
    if (lineEndpoints.length < 2) return [];
    const [start, end] = lineEndpoints;
    return linePreviewRatios().map((ratio) => interpolateLatLng(start, end, ratio));
  }

  async function loadHtml2Canvas() {
    if (!html2canvasLoader) {
      html2canvasLoader = import("html2canvas").then((module) => module.default || module);
    }
    return html2canvasLoader;
  }

  function hasVisibleRasterImagery() {
    const mapContainer = map?.getContainer?.();
    if (!mapContainer) return false;
    return !!mapContainer.querySelector(".leaflet-tile-pane img, .leaflet-tile-pane canvas");
  }

  async function captureVisibleImageryCanvas() {
    const mapContainer = map?.getContainer?.();
    if (!mapContainer) throw new Error("Map container is not available.");
    const html2canvas = await loadHtml2Canvas();
    return html2canvas(mapContainer, {
      backgroundColor: null,
      useCORS: true,
      logging: false,
      scale: 1,
      ignoreElements(element) {
        const className = String(element?.className?.baseVal || element?.className || "");
        if (className.includes("leaflet-control")) return true;
        if (className.includes("leaflet-overlay-pane")) return true;
        if (className.includes("leaflet-marker-pane")) return true;
        if (className.includes("leaflet-tooltip-pane")) return true;
        if (className.includes("leaflet-popup-pane")) return true;
        return false;
      },
    });
  }

  async function estimateLineSlipCountFromImagery() {
    if (lineEndpoints.length < 2) {
      imageryEstimateStatus = "Draw a line first.";
      imageryEstimateDebug = null;
      render();
      return;
    }
    imageryEstimateStatus = "Checking the visible imagery along this line...";
    render();
    const [start, end] = lineEndpoints;
    const lineLengthFeet = (Number(map.distance([start.lat, start.lng], [end.lat, end.lng])) || 0) * 3.280839895;
    if (!Number.isFinite(lineLengthFeet) || lineLengthFeet <= 0) {
      imageryEstimateStatus = "Could not determine line length from the current map.";
      render();
      return;
    }
    if (!hasVisibleRasterImagery()) {
      imageryEstimateStatus = "No readable aerial imagery layer is visible on the map.";
      imageryEstimateDebug = null;
      render();
      return;
    }
    const startPoint = map.latLngToContainerPoint([start.lat, start.lng]);
    const endPoint = map.latLngToContainerPoint([end.lat, end.lng]);
    const lineLengthPx = Math.hypot(Number(endPoint.x) - Number(startPoint.x), Number(endPoint.y) - Number(startPoint.y));
    if (!Number.isFinite(lineLengthPx) || lineLengthPx < 40) {
      imageryEstimateStatus = "Zoom in a bit more before estimating from imagery.";
      imageryEstimateDebug = null;
      render();
      return;
    }
    imageryEstimateStatus = "Analyzing repeating imagery pattern along the line...";
    render();
    const canvas = await captureVisibleImageryCanvas();
    const context = canvas?.getContext?.("2d", { willReadFrequently: true });
    if (!context) {
      imageryEstimateStatus = "Could not read the visible imagery layer.";
      imageryEstimateDebug = null;
      render();
      return;
    }
    const width = Number(canvas.width) || 0;
    const height = Number(canvas.height) || 0;
    if (!width || !height) {
      imageryEstimateStatus = "Visible imagery capture returned an empty canvas.";
      imageryEstimateDebug = null;
      render();
      return;
    }
    const pixels = context.getImageData(0, 0, width, height).data;
    const sampleCount = clampNumber(Math.round(lineLengthPx), 80, 420);
    const pixelsPerFoot = lineLengthPx / Math.max(1, lineLengthFeet);
    const minPitchFeet = 12;
    const maxPitchFeet = 30;
    const minLag = clampNumber(Math.round(minPitchFeet * pixelsPerFoot), 6, Math.max(12, Math.round(lineLengthPx / 2)));
    const maxLag = clampNumber(Math.round(maxPitchFeet * pixelsPerFoot), minLag + 2, Math.max(minLag + 2, Math.round(lineLengthPx / 2)));
    const corridorHalfWidths = [
      clampNumber(Math.round(lineLengthPx * 0.02), 7, 12),
      clampNumber(Math.round(lineLengthPx * 0.03), 9, 16),
      clampNumber(Math.round(lineLengthPx * 0.04), 11, 22),
      clampNumber(Math.round(lineLengthPx * 0.05), 13, 28),
    ];
    const signalRuns = corridorHalfWidths.map((corridorHalfWidthPx) => {
      const signal = buildImagerySpacingSignal(pixels, width, height, startPoint, endPoint, {
        sampleCount,
        crossSampleCount: 15,
        corridorHalfWidthPx,
      });
      const estimate = estimatePitchFromSignal(signal, { minLag, maxLag });
      return {
        corridorHalfWidthPx,
        signal,
        estimate: estimate ? { ...estimate, corridorHalfWidthPx } : null,
      };
    });
    const candidateEstimates = signalRuns.map((run) => run.estimate).filter(Boolean);
    const lagEstimate = chooseConsensusPitchEstimate(candidateEstimates);
    if (!lagEstimate?.lag) {
      imageryEstimateStatus = "Could not find a reliable repeating spacing in the imagery.";
      imageryEstimateDebug = {
        signalRuns,
        chosenCorridorHalfWidthPx: null,
        chosenSignal: [],
        suggestedSpacingSamples: null,
      };
      render();
      return;
    }
    const chosenRun = signalRuns.reduce((best, run) => {
      if (!run?.estimate) return best;
      if (Math.abs(run.estimate.lag - lagEstimate.lag) > Math.max(3, lagEstimate.lag * 0.2)) return best;
      if (!best) return run;
      return (run.estimate.confidence || 0) > (best.estimate?.confidence || 0) ? run : best;
    }, null);
    const currentCount = Math.max(2, Number(state.lineSlipCount || 2) || 2);
    const estimatedPitchFeet = lagEstimate.lag / Math.max(1e-6, pixelsPerFoot);
    const estimatedCount = Math.max(2, Math.round(lineLengthFeet / Math.max(1e-6, estimatedPitchFeet)) + 1);
    if (!Number.isFinite(estimatedCount) || estimatedCount < 2 || estimatedCount > 500) {
      imageryEstimateStatus = "Imagery estimate landed outside a safe slip-count range.";
      render();
      return;
    }
    const allowedDelta = Math.max(2, Math.round(currentCount * 0.35));
    if (lagEstimate.confidence < 0.28 && Math.abs(estimatedCount - currentCount) > allowedDelta) {
      imageryEstimateStatus = `Imagery pattern was too weak to safely move from ${currentCount} to ${estimatedCount} slips. Try zooming in more or draw the line tighter along one row.`;
      render();
      return;
    }
    state.lineSlipCount = estimatedCount;
    persist();
    renderPreview();
    imageryEstimateDebug = {
      signalRuns,
      chosenCorridorHalfWidthPx: chosenRun?.corridorHalfWidthPx || null,
      chosenSignal: chosenRun?.signal || [],
      suggestedSpacingSamples: lagEstimate.lag,
    };
    const confidenceLabel = lagEstimate.confidence >= 0.66
      ? "high"
      : lagEstimate.confidence >= 0.4
        ? "medium"
        : "low";
    imageryEstimateStatus = `Estimated ${estimatedCount} slips from imagery at about ${estimatedPitchFeet.toFixed(1)} ft spacing (${confidenceLabel} confidence, ${lagEstimate.method || "signal"} method, ${lagEstimate.samples || candidateEstimates.length || 1} passes agreed).`;
    status = `Updated line preview to ${estimatedCount} slips from imagery.`;
    render();
  }

  function renderPreview() {
    previewLayer.clearLayers();
    const previewPoints = linePreviewPoints();
    const guidePoints = lineLayoutMode === "spacing"
      ? [...lineEndpoints, ...(lineHoverPoint && lineEndpoints.length >= 2 && lineEndpoints.length < 3 ? [lineHoverPoint] : [])]
      : lineEndpoints;
    if (lineEndpoints.length === 1) {
      L.circleMarker([lineEndpoints[0].lat, lineEndpoints[0].lng], {
        radius: 6,
        color: "#f59e0b",
        weight: 2,
        fillColor: "#f59e0b",
        fillOpacity: 0.35,
      }).addTo(previewLayer);
      return;
    }
    if (guidePoints.length >= 2) {
      L.polyline(guidePoints.map((point) => [point.lat, point.lng]), {
        color: "#f59e0b",
        weight: 2,
        dashArray: "6 6",
      }).addTo(previewLayer);
      lineEndpoints.forEach((point, index) => {
        const handle = L.marker([point.lat, point.lng], {
          draggable: true,
          autoPan: true,
          zIndexOffset: 1000,
          icon: L.divIcon({
            className: "slip-inventory-endpoint-handle",
            iconSize: [18, 18],
            iconAnchor: [9, 9],
            html: `
              <div style="
                width:18px;
                height:18px;
                border-radius:999px;
                background:rgba(245,158,11,0.96);
                border:2px solid rgba(15,23,42,0.92);
                box-shadow:0 0 0 1px rgba(254,243,199,0.65), 0 6px 12px rgba(15,23,42,0.24);
              "></div>
            `,
          }),
        }).addTo(previewLayer);
        const tooltip = lineLayoutMode === "spacing"
          ? (index === 0 ? "Drag first slip center" : index === 1 ? "Drag spacing slip center" : "Drag run extent")
          : (index === 0 ? "Drag start point" : "Drag end point");
        handle.bindTooltip(tooltip, { direction: "top", offset: [0, -8] });
        handle.on("dragend", (event) => {
          const latlng = event?.target?.getLatLng?.();
          if (!latlng) return;
          lineEndpoints[index] = { lat: Number(latlng.lat), lng: Number(latlng.lng) };
          status = lineLayoutMode === "spacing"
            ? "Adjusted spacing run point."
            : `Adjusted ${index === 0 ? "start" : "end"} point.`;
          renderPreview();
          render();
        });
      });
    }
    if (!previewPoints.length) return;
    const ids = previewSlipIds(state, previewPoints.length);
    previewPoints.forEach((point, index) => {
      const marker = L.marker([point.lat, point.lng], {
        interactive: true,
        icon: L.divIcon({
          className: "slip-inventory-preview-label-wrap",
          iconSize: null,
          iconAnchor: [0, 0],
          html: `
            <div style="transform:translate(-50%, -50%); display:flex; flex-direction:column; align-items:center; gap:4px;">
              <div style="width:16px; height:2px; background:rgba(245,158,11,0.95); box-shadow:0 0 0 1px rgba(15,23,42,0.28);"></div>
              <div style="
                display:inline-flex;
                align-items:center;
                justify-content:center;
                padding:2px 7px;
                border-radius:999px;
                background:rgba(15,23,42,0.78);
                color:#fde68a;
                border:1px solid rgba(245,158,11,0.55);
                font:700 11px/1.2 system-ui,sans-serif;
                white-space:nowrap;
              ">${esc(ids[index])}</div>
            </div>
          `,
        }),
      }).addTo(previewLayer);
      marker.bindTooltip(`Preview ${ids[index]}${index < previewPoints.length - 1 ? ` | click to set count ${index + 1}` : ""}`, { direction: "top", offset: [0, -8] });
      marker.on("click", () => {
        if (lineLayoutMode === "spacing") return;
        state.lineSlipCount = Math.max(2, index + 1);
        persist();
        renderPreview();
        render();
      });
    });
  }

  function syncSelection() {
    if (state.selectedMeasurementId && records.some((record) => record.measurement_id === state.selectedMeasurementId)) return;
    state.selectedMeasurementId = records[0]?.measurement_id || "";
    persist();
  }

  function renderMap() {
    markersLayer.clearLayers();
    records.forEach((record) => {
      const point = parsePointWkt(record.geometry_wkt);
      if (!point) return;
      const selected = record.measurement_id === state.selectedMeasurementId;
      const label = record.slip_id || record.measurement_id;
      const marker = L.marker([point.lat, point.lng], {
        interactive: true,
        icon: L.divIcon({
          className: "slip-inventory-map-label-wrap",
          iconSize: null,
          iconAnchor: [0, 0],
          html: `
            <div style="
              display:inline-flex;
              align-items:center;
              justify-content:center;
              padding:3px 8px;
              border-radius:999px;
              background:${selected ? "rgba(245,158,11,0.92)" : "rgba(15,23,42,0.88)"};
              color:${selected ? "#111827" : "#e2e8f0"};
              border:1px solid ${selected ? "rgba(254,243,199,0.95)" : "rgba(56,189,248,0.55)"};
              box-shadow:${selected ? "0 0 0 1px rgba(245,158,11,0.28)" : "0 6px 14px rgba(15,23,42,0.22)"};
              font:700 12px/1.2 system-ui,sans-serif;
              white-space:nowrap;
              transform:translate(-50%, -50%);
              pointer-events:auto;
            ">${esc(label)}</div>
          `,
        }),
      }).addTo(markersLayer);
      marker.bindTooltip(`${record.slip_id || record.measurement_id}`, { direction: "top", offset: [0, -6] });
      marker.on("click", () => {
        state.selectedMeasurementId = record.measurement_id;
        persist();
        render();
      });
    });
    renderPreview();
  }

  function renderBottomPanel() {
    const current = selectedRecord();
    const rows = records.map((record) => `
      <tr data-slip-row="${esc(record.measurement_id)}" style="cursor:pointer; background:${record.measurement_id === state.selectedMeasurementId ? "rgba(245,158,11,0.12)" : "transparent"};">
        <td style="padding:6px 8px;">${esc(record.slip_id || "")}</td>
        <td style="padding:6px 8px;">${esc(record.marina_name || record.marina_id || "")}</td>
        <td style="padding:6px 8px;">${esc(record.vendor_slip_ref || "")}</td>
        <td style="padding:6px 8px;">${esc(record.measured_linear_feet ?? "")}</td>
        <td style="padding:6px 8px;">${esc(record.measured_width_feet ?? "")}</td>
        <td style="padding:6px 8px;">${esc(record.measured_depth_feet ?? "")}</td>
        <td style="padding:6px 8px;">${esc(record.measurement_date || "")}</td>
        <td style="padding:6px 8px;">${esc(record.confidence_score || "")}</td>
      </tr>
    `).join("");

    app.ui.mountBottomPanel(`
      <div id="slip-inventory-bottom" style="padding:10px 10px 0 10px;">
        <div class="card" style="padding:10px;">
          <div class="row" style="justify-content:space-between; align-items:center; gap:8px; margin-bottom:8px;">
            <div>
              <div class="lm-title">Slip Inventory</div>
              <div class="small">${esc(records.length ? `${records.length} record${records.length === 1 ? "" : "s"} loaded` : "No database records loaded yet.")}</div>
            </div>
            <div class="row" style="gap:8px;">
              <button id="slip-bottom-refresh" class="btn">Refresh</button>
              <button id="slip-bottom-show-selected" class="btn"${current ? "" : " disabled"}>Show Selected</button>
            </div>
          </div>
          <div style="max-height:300px; overflow:auto; border:1px solid rgba(148,163,184,.18); border-radius:8px;">
            <table style="width:100%; border-collapse:collapse; font-size:12px;">
              <thead style="position:sticky; top:0; background:rgba(15,23,42,.92); z-index:1;">
                <tr>
                  <th style="text-align:left; padding:6px 8px;">Slip ID</th>
                  <th style="text-align:left; padding:6px 8px;">Marina</th>
                  <th style="text-align:left; padding:6px 8px;">Vendor Ref</th>
                  <th style="text-align:left; padding:6px 8px;">Linear</th>
                  <th style="text-align:left; padding:6px 8px;">Width</th>
                  <th style="text-align:left; padding:6px 8px;">Depth</th>
                  <th style="text-align:left; padding:6px 8px;">Date</th>
                  <th style="text-align:left; padding:6px 8px;">Confidence</th>
                </tr>
              </thead>
              <tbody>
                ${rows || `<tr><td colspan="8" class="small" style="padding:8px;">No slip inventory rows found.</td></tr>`}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    `, {
      ownerId: "slip-inventory",
      title: "Slip Inventory",
      onShow: bindBottomPanelControls,
    });
  }

  async function refreshRecords({ quiet = false } = {}) {
    try {
      const payload = await requestJson(`${API_BASE}?limit=5000`);
      records = Array.isArray(payload?.items) ? payload.items.map((item) => normalizeRecord(item)) : [];
      syncSelection();
      renderMap();
      renderBottomPanel();
      if (!quiet) status = `Loaded ${records.length} slip inventory record${records.length === 1 ? "" : "s"}.`;
      render();
    } catch (error) {
      status = error?.message || "Failed to load slip inventory records.";
      render();
      throw error;
    }
  }

  function stopPointDrop(reason = "stopped") {
    if (pointDropOff) {
      pointDropOff();
      pointDropOff = null;
    }
    pointDropActive = false;
    try { map.getContainer().style.cursor = ""; } catch {}
    if (reason === "cancelled") {
      status = "Point drop cancelled.";
      render();
    }
  }

  function stopLineLayout(reason = "stopped") {
    if (lineLayoutOff) {
      lineLayoutOff();
      lineLayoutOff = null;
    }
    lineLayoutActive = false;
    lineEndpoints = [];
    lineLayoutMode = "endpoint";
    lineHoverPoint = null;
    imageryEstimateDebug = null;
    try { map.getContainer().style.cursor = ""; } catch {}
    renderPreview();
    if (reason === "cancelled") {
      status = "Line layout cancelled.";
      render();
    }
  }

  async function createRecordFromPoint(latlng) {
    const measurementId = makeId("MEAS");
    const slipId = buildSlipIdFromState(state) || `SLIP-${String(selectedIndex() + 1).padStart(4, "0")}`;
    const payload = {
      measurement_id: measurementId,
      marina_id: state.marinaId,
      marina_name: state.marinaName,
      slip_id: slipId,
      vendor_slip_ref: "",
      measured_linear_feet: null,
      measured_width_feet: null,
      measured_depth_feet: null,
      measurement_date: state.measurementDate,
      confidence_score: state.confidenceScore,
      record_created_timestamp: new Date().toISOString(),
      lat: Number(latlng.lat),
      lng: Number(latlng.lng),
    };
    const response = await requestJson(`${API_BASE}/from-point`, {
      method: "POST",
      body: payload,
    });
    state.selectedMeasurementId = response?.item?.measurement_id || measurementId;
    incrementSlipIdNumber(state);
    persist();
    await refreshRecords({ quiet: true });
    status = `Created ${slipId} from map point.`;
    render();
  }

  async function createRecordsFromPoints(points = []) {
    const ids = previewSlipIds(state, points.length);
    for (let index = 0; index < points.length; index += 1) {
      const point = points[index];
      const measurementId = makeId("MEAS");
      const slipId = ids[index] || `SLIP-${String(selectedIndex() + index + 1).padStart(4, "0")}`;
      await requestJson(`${API_BASE}/from-point`, {
        method: "POST",
        body: {
          measurement_id: measurementId,
          marina_id: state.marinaId,
          marina_name: state.marinaName,
          slip_id: slipId,
          vendor_slip_ref: "",
          measured_linear_feet: null,
          measured_width_feet: null,
          measured_depth_feet: null,
          measurement_date: state.measurementDate,
          confidence_score: state.confidenceScore,
          record_created_timestamp: new Date().toISOString(),
          lat: Number(point.lat),
          lng: Number(point.lng),
        },
      });
      incrementSlipIdNumber(state);
    }
    persist();
    await refreshRecords({ quiet: true });
    status = `Created ${points.length} slips from line preview.`;
    render();
  }

  function startPointDrop() {
    if (pointDropActive) return;
    stopLineLayout("replaced");
    pointDropActive = true;
    const mapContainer = map?.getContainer?.() || null;
    try { if (mapContainer) mapContainer.style.cursor = "crosshair"; } catch {}
    let pointerDown = null;
    let pointerMoved = false;
    const moveThreshold = 6;

    const onPointerDown = (event) => {
      if (event.button != null && event.button !== 0) return;
      pointerDown = {
        x: Number(event.clientX),
        y: Number(event.clientY),
      };
      pointerMoved = false;
    };

    const onPointerMove = (event) => {
      if (!pointerDown) return;
      const dx = Math.abs(Number(event.clientX) - pointerDown.x);
      const dy = Math.abs(Number(event.clientY) - pointerDown.y);
      if (dx > moveThreshold || dy > moveThreshold) {
        pointerMoved = true;
      }
    };

    const onPointerUp = async (event) => {
      if (!pointerDown) return;
      const latlng = map?.mouseEventToLatLng?.(event);
      const shouldCreate = !pointerMoved && latlng;
      pointerDown = null;
      pointerMoved = false;
      if (!shouldCreate) return;
      try {
        await createRecordFromPoint(latlng);
        app.ui.toast("Slip inventory point created. Click again to continue.");
      } catch (error) {
        status = error?.message || "Failed to create slip inventory record.";
        app.ui.toast(status);
        render();
      }
    };
    const onKey = (event) => {
      if (event.key === "Escape") {
        document.removeEventListener("keydown", onKey);
        mapContainer?.removeEventListener?.("pointerdown", onPointerDown, true);
        mapContainer?.removeEventListener?.("pointermove", onPointerMove, true);
        mapContainer?.removeEventListener?.("pointerup", onPointerUp, true);
        mapContainer?.removeEventListener?.("pointercancel", onPointerUp, true);
        stopPointDrop("cancelled");
      }
    };
    pointDropOff = () => {
      mapContainer?.removeEventListener?.("pointerdown", onPointerDown, true);
      mapContainer?.removeEventListener?.("pointermove", onPointerMove, true);
      mapContainer?.removeEventListener?.("pointerup", onPointerUp, true);
      mapContainer?.removeEventListener?.("pointercancel", onPointerUp, true);
      document.removeEventListener("keydown", onKey);
    };
    mapContainer?.addEventListener?.("pointerdown", onPointerDown, true);
    mapContainer?.addEventListener?.("pointermove", onPointerMove, true);
    mapContainer?.addEventListener?.("pointerup", onPointerUp, true);
    mapContainer?.addEventListener?.("pointercancel", onPointerUp, true);
    document.addEventListener("keydown", onKey);
    status = "Click to create slips. Click and drag pans the map. Press Esc to cancel.";
    render();
  }

  function startLineLayout(mode = "endpoint") {
    if (lineLayoutActive) return;
    stopPointDrop("replaced");
    lineLayoutActive = true;
    lineLayoutMode = mode === "spacing" ? "spacing" : "endpoint";
    lineEndpoints = [];
    lineHoverPoint = null;
    imageryEstimateStatus = "";
    imageryEstimateDebug = null;
    const mapContainer = map?.getContainer?.() || null;
    try { if (mapContainer) mapContainer.style.cursor = "crosshair"; } catch {}
    let pointerDown = null;
    let pointerMoved = false;
    const moveThreshold = 6;
    const onPointerDown = (event) => {
      if (event.button != null && event.button !== 0) return;
      pointerDown = { x: Number(event.clientX), y: Number(event.clientY) };
      pointerMoved = false;
    };
    const onPointerMove = (event) => {
      if (lineLayoutMode === "spacing" && lineEndpoints.length >= 2 && lineEndpoints.length < 3) {
        const latlng = map?.mouseEventToLatLng?.(event);
        if (latlng) {
          lineHoverPoint = { lat: Number(latlng.lat), lng: Number(latlng.lng) };
          renderPreview();
        }
      }
      if (pointerDown) {
        const dx = Math.abs(Number(event.clientX) - pointerDown.x);
        const dy = Math.abs(Number(event.clientY) - pointerDown.y);
        if (dx > moveThreshold || dy > moveThreshold) pointerMoved = true;
      }
    };
    const onWheel = (event) => {
      if (lineLayoutMode === "spacing") return;
      if (!event.ctrlKey || lineEndpoints.length < 2) return;
      if (!mapContainer?.contains?.(event.target)) return;
      event.preventDefault?.();
      event.stopPropagation?.();
      event.stopImmediatePropagation?.();
      const direction = Number(event.deltaY) > 0 ? -1 : 1;
      state.lineSlipCount = Math.max(2, Number(state.lineSlipCount || 2) + direction);
      persist();
      renderPreview();
      render();
    };
    const onPointerUp = (event) => {
      if (!pointerDown) return;
      const latlng = map?.mouseEventToLatLng?.(event);
      const shouldCapture = !pointerMoved && latlng;
      pointerDown = null;
      pointerMoved = false;
      if (!shouldCapture) return;
      if (lineLayoutMode === "spacing") {
        if (lineEndpoints.length === 0) {
          lineEndpoints = [{ lat: Number(latlng.lat), lng: Number(latlng.lng) }];
          status = "First slip center captured. Click the next slip center to set spacing.";
        } else if (lineEndpoints.length === 1) {
          lineEndpoints = [lineEndpoints[0], { lat: Number(latlng.lat), lng: Number(latlng.lng) }];
          lineHoverPoint = { lat: Number(latlng.lat), lng: Number(latlng.lng) };
          status = "Spacing captured. Move along the pier to preview labels, then click the end of the run.";
        } else {
          lineEndpoints = [lineEndpoints[0], lineEndpoints[1], { lat: Number(latlng.lat), lng: Number(latlng.lng) }];
          lineHoverPoint = null;
          state.lineSlipCount = linePreviewPoints().length;
          persist();
          status = `Spacing run ready for ${state.lineSlipCount} slips. Press Enter or Generate Line.`;
        }
      } else {
        if (lineEndpoints.length === 0) {
          lineEndpoints = [{ lat: Number(latlng.lat), lng: Number(latlng.lng) }];
          status = "Line start captured. Click the end point.";
        } else {
          lineEndpoints = [lineEndpoints[0], { lat: Number(latlng.lat), lng: Number(latlng.lng) }];
          status = `Line preview ready for ${state.lineSlipCount} slips. Adjust the count, optionally use Auto Estimate From Imagery, or generate.`;
        }
      }
      renderPreview();
      render();
    };
    const onKey = (event) => {
      const canGenerate = lineLayoutMode === "spacing" ? lineEndpoints.length >= 3 : lineEndpoints.length >= 2;
      if (event.key === "Enter" && canGenerate) {
        event.preventDefault?.();
        const points = linePreviewPoints();
        if (points.length >= 2) {
          createRecordsFromPoints(points).then(() => {
            stopLineLayout("completed");
            app.ui.toast(`Generated ${points.length} slips from line preview.`);
          }).catch((error) => {
            status = error?.message || "Failed to generate line slips.";
            app.ui.toast(status);
            render();
          });
        }
        return;
      }
      if (event.key === "Escape") {
        mapContainer?.removeEventListener?.("pointerdown", onPointerDown, true);
        mapContainer?.removeEventListener?.("pointermove", onPointerMove, true);
        mapContainer?.removeEventListener?.("pointerup", onPointerUp, true);
        mapContainer?.removeEventListener?.("pointercancel", onPointerUp, true);
        window.removeEventListener("wheel", onWheel, { capture: true });
        stopLineLayout("cancelled");
      }
    };
    lineLayoutOff = () => {
      mapContainer?.removeEventListener?.("pointerdown", onPointerDown, true);
      mapContainer?.removeEventListener?.("pointermove", onPointerMove, true);
      mapContainer?.removeEventListener?.("pointerup", onPointerUp, true);
      mapContainer?.removeEventListener?.("pointercancel", onPointerUp, true);
      window.removeEventListener("wheel", onWheel, { capture: true });
      document.removeEventListener("keydown", onKey);
    };
    mapContainer?.addEventListener?.("pointerdown", onPointerDown, true);
    mapContainer?.addEventListener?.("pointermove", onPointerMove, true);
    mapContainer?.addEventListener?.("pointerup", onPointerUp, true);
    mapContainer?.addEventListener?.("pointercancel", onPointerUp, true);
    window.addEventListener("wheel", onWheel, { capture: true, passive: false });
    document.addEventListener("keydown", onKey);
    status = lineLayoutMode === "spacing"
      ? "Click the first slip center, then the next slip center to set spacing."
      : "Click the first slip location, then click the last slip location. Use Ctrl+mouse wheel to adjust slip count after the line is set.";
    renderPreview();
    render();
  }

  async function saveSelectedRecord() {
    const current = selectedRecord();
    if (!current) return;
    const selectedMarina = marinaOptionForId(
      String(containerEl?.querySelector("#slip-marina-id")?.value || ""),
      String(containerEl?.querySelector("#slip-marina-name")?.value || ""),
    );
    const nextRecord = {
      ...current,
      marina_id: String(selectedMarina?.marinaId || ""),
      marina_name: String(selectedMarina?.marinaName || ""),
      slip_id: String(containerEl?.querySelector("#slip-slip-id")?.value || ""),
      vendor_slip_ref: String(containerEl?.querySelector("#slip-vendor-ref")?.value || ""),
      measured_linear_feet: containerEl?.querySelector("#slip-linear-feet")?.value === "" ? null : Number(containerEl?.querySelector("#slip-linear-feet")?.value),
      measured_width_feet: containerEl?.querySelector("#slip-width-feet")?.value === "" ? null : Number(containerEl?.querySelector("#slip-width-feet")?.value),
      measured_depth_feet: containerEl?.querySelector("#slip-depth-feet")?.value === "" ? null : Number(containerEl?.querySelector("#slip-depth-feet")?.value),
      measurement_date: String(containerEl?.querySelector("#slip-measurement-date")?.value || ""),
      confidence_score: String(containerEl?.querySelector("#slip-confidence")?.value || "High"),
      geometry_wkt: String(current.geometry_wkt || buildPointWkt(0, 0)),
      record_created_timestamp: String(current.record_created_timestamp || new Date().toISOString()),
    };
    await requestJson(`${API_BASE}/${encodeURIComponent(current.measurement_id)}`, {
      method: "PATCH",
      body: nextRecord,
    });
    state.marinaId = nextRecord.marina_id;
    state.marinaName = nextRecord.marina_name;
    state.measurementDate = nextRecord.measurement_date;
    state.confidenceScore = nextRecord.confidence_score;
    persist();
    await refreshRecords({ quiet: true });
    status = `Saved ${nextRecord.slip_id}.`;
    render();
  }

  async function deleteSelectedRecord() {
    const current = selectedRecord();
    if (!current) return;
    await requestJson(`${API_BASE}/${encodeURIComponent(current.measurement_id)}`, {
      method: "DELETE",
    });
    status = `Deleted ${current.slip_id || current.measurement_id}.`;
    state.selectedMeasurementId = "";
    persist();
    await refreshRecords({ quiet: true });
    render();
  }

  function exportJson() {
    downloadText("slip_inventory.json", JSON.stringify(records, null, 2), "application/json;charset=utf-8");
  }

  function exportCsv() {
    downloadText("slip_inventory.csv", buildCsv(records), "text/csv;charset=utf-8");
  }

  function render() {
    renderMap();
    renderBottomPanel();
    if (!containerEl) return;
    const current = selectedRecord();
    const currentMarina = marinaOptionForId(current?.marina_id, current?.marina_name);
    const defaultMarina = marinaOptionForId(state.marinaId, state.marinaName);
    const defaultOptions = [
      defaultMarina,
      ...marinaCatalog,
    ].filter((entry, index, arr) => entry && arr.findIndex((candidate) => candidate?.marinaId === entry.marinaId) === index);
    const currentOptions = [
      currentMarina,
      ...marinaCatalog,
    ].filter((entry, index, arr) => entry && arr.findIndex((candidate) => candidate?.marinaId === entry.marinaId) === index);
    const previewPoints = linePreviewPoints();
    const previewIds = previewSlipIds(state, previewPoints.length);
    const canGenerateLine = lineLayoutMode === "spacing"
      ? lineEndpoints.length >= 3 && previewPoints.length >= 2
      : lineEndpoints.length >= 2 && previewPoints.length >= 2;
    const canEstimateImagery = lineLayoutMode !== "spacing" && lineEndpoints.length >= 2;
    const chartSignal = Array.isArray(imageryEstimateDebug?.chosenSignal) ? imageryEstimateDebug.chosenSignal : [];
    const chartWidth = 640;
    const chartHeight = 180;
    const chartPadding = 12;
    const chartPath = buildSparklinePath(chartSignal, chartWidth, chartHeight, chartPadding);
    const chartMin = chartSignal.length ? Math.min(...chartSignal) : 0;
    const chartMax = chartSignal.length ? Math.max(...chartSignal) : 0;
    const chartRange = Math.max(1e-6, chartMax - chartMin);
    const liveCenterRatios = previewPoints.length >= 2 ? linePreviewRatios() : [];
    const chartCenterLines = liveCenterRatios.map((ratio) => {
      const x = chartPadding + (ratio * (chartWidth - (chartPadding * 2)));
      return `<line x1="${x.toFixed(1)}" y1="${chartPadding}" x2="${x.toFixed(1)}" y2="${(chartHeight - chartPadding).toFixed(1)}" stroke="rgba(245,158,11,0.55)" stroke-width="1.5" stroke-dasharray="4 4" />`;
    }).join("");
    const chartPeakDots = chartSignal.map((value, index) => {
      const x = chartPadding + ((index / Math.max(1, chartSignal.length - 1)) * (chartWidth - (chartPadding * 2)));
      const y = chartHeight - chartPadding - (((value - chartMin) / chartRange) * (chartHeight - (chartPadding * 2)));
      return `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="1.6" fill="rgba(147,197,253,0.8)" />`;
    }).join("");
    containerEl.innerHTML = `
      <div class="tool-section">
        <div class="small">Create database records by dropping a point on the map, then edit the current export-schema fields here.</div>
        <div class="row" style="margin-top:8px; gap:8px; flex-wrap:wrap; align-items:flex-end;">
          <label class="small" style="flex:1;">Marina ID
            <div class="row" style="gap:8px; align-items:center;">
              <select id="slip-default-marina-id" style="flex:1;">
                <option value="">Select marina</option>
                ${defaultOptions.map((entry) => `<option value="${esc(entry.marinaId)}"${entry.marinaId === state.marinaId ? " selected" : ""}>${esc(entry.marinaId)}</option>`).join("")}
              </select>
              <button id="slip-default-marina-zoom" type="button" class="btn"${defaultMarina && Number.isFinite(defaultMarina.lat) && Number.isFinite(defaultMarina.lng) ? "" : " disabled"}>Zoom To</button>
            </div>
          </label>
          <label class="small" style="flex:1;">Marina Name
            <input id="slip-default-marina-name" type="text" value="${esc(state.marinaName)}" readonly />
          </label>
        </div>
        <div class="row" style="margin-top:8px; gap:8px; flex-wrap:wrap; align-items:flex-end;">
          <label class="small">Measurement Date
            <input id="slip-default-measurement-date" type="date" value="${esc(state.measurementDate)}" />
          </label>
          <label class="small">Confidence
            <select id="slip-default-confidence">
              <option value="High"${state.confidenceScore === "High" ? " selected" : ""}>High</option>
              <option value="Medium"${state.confidenceScore === "Medium" ? " selected" : ""}>Medium</option>
              <option value="Low"${state.confidenceScore === "Low" ? " selected" : ""}>Low</option>
            </select>
          </label>
          <label class="small">Slip ID Part 1
            <input id="slip-default-slipid-part1" type="text" value="${esc(state.slipIdPart1)}" style="width:70px;" />
          </label>
          <label class="small">Part 2
            <input id="slip-default-slipid-part2" type="text" value="${esc(state.slipIdPart2)}" style="width:70px;" />
          </label>
          <label class="small">Part 3
            <input id="slip-default-slipid-part3" type="text" value="${esc(state.slipIdPart3)}" style="width:90px;" />
          </label>
          <label class="small" style="display:flex; align-items:center; gap:6px;">
            <input id="slip-default-slipid-autoinc" type="checkbox" ${state.slipIdAutoIncrement ? "checked" : ""} />
            Auto increment
          </label>
          <label class="small">Step
            <input id="slip-default-slipid-step" type="number" min="1" step="1" value="${esc(state.slipIdIncrementBy)}" style="width:70px;" />
          </label>
          <label class="small">Line Slips
            <input id="slip-default-line-count" type="number" min="2" step="1" value="${esc(state.lineSlipCount)}" style="width:80px;" />
          </label>
          <div class="small" style="padding-bottom:6px; min-width:170px;">Next Slip ID: <strong>${esc(buildSlipIdFromState(state) || "auto")}</strong></div>
          <div class="row" style="margin-left:auto; gap:8px; align-items:flex-end;">
            <button id="slip-drop-point" type="button" class="btn primary">${pointDropActive ? "Click Map..." : "Drop Point"}</button>
            <button id="slip-stop-drop" type="button" class="btn"${pointDropActive ? "" : " disabled"}>Stop</button>
            <button id="slip-line-layout" type="button" class="btn primary">${lineLayoutActive ? "Layout Active..." : "Draw Line"}</button>
            <button id="slip-spacing-run-layout" type="button" class="btn primary"${lineLayoutActive ? " disabled" : ""}>Spacing Run</button>
            <button id="slip-line-clear" type="button" class="btn"${lineEndpoints.length ? "" : " disabled"}>Clear Line</button>
            <button id="slip-line-estimate-imagery" type="button" class="btn"${canEstimateImagery ? "" : " disabled"}>Auto Estimate From Imagery</button>
            <button id="slip-line-generate" type="button" class="btn"${canGenerateLine ? "" : " disabled"}>Generate Line</button>
            <button id="slip-refresh" type="button" class="btn">Refresh</button>
            <button id="slip-export-csv" type="button" class="btn"${records.length ? "" : " disabled"}>CSV</button>
            <button id="slip-export-json" type="button" class="btn"${records.length ? "" : " disabled"}>JSON</button>
          </div>
        </div>
        ${lineEndpoints.length ? `
          <div class="small" style="margin-top:8px;">
            ${lineLayoutMode === "spacing"
              ? (lineEndpoints.length === 1
                ? "First slip center set. Click the next slip center to measure spacing."
                : lineEndpoints.length === 2
                  ? `Spacing measured. Move along the pier to preview ${previewPoints.length} slips, then click the end of the run.`
                  : `Spacing run ready with ${previewPoints.length} slips. Press Enter or Generate Line.`)
              : (lineEndpoints.length === 1
                ? "Line start set. Click the end point on the map."
                : `Previewing ${previewPoints.length} slips along the line. Click a preview mark to snap the count to that position.`)}
          </div>
          ${previewIds.length ? `<div class="small" style="margin-top:4px;">Preview IDs: ${esc(previewIds.join(", "))}</div>` : ""}
        ` : ""}
        ${imageryEstimateStatus ? `<div class="small" style="margin-top:6px; color:#93c5fd;">${esc(imageryEstimateStatus)}</div>` : ""}
        ${chartSignal.length ? `
          <div style="margin-top:8px; padding:8px; border:1px solid rgba(148,163,184,.18); border-radius:8px; background:rgba(15,23,42,.35);">
            <div class="small" style="margin-bottom:6px;">Imagery signal debug: blue line is sampled imagery intensity change, dashed orange lines are suggested slip centers.</div>
            <svg viewBox="0 0 ${chartWidth} ${chartHeight}" style="display:block; width:100%; height:auto;">
              <rect x="0" y="0" width="${chartWidth}" height="${chartHeight}" rx="8" fill="rgba(15,23,42,0.5)" />
              <line x1="${chartPadding}" y1="${(chartHeight - chartPadding).toFixed(1)}" x2="${(chartWidth - chartPadding).toFixed(1)}" y2="${(chartHeight - chartPadding).toFixed(1)}" stroke="rgba(148,163,184,0.35)" stroke-width="1" />
              ${chartCenterLines}
              <path d="${chartPath}" fill="none" stroke="rgba(147,197,253,0.95)" stroke-width="2.5" stroke-linejoin="round" stroke-linecap="round" />
              ${chartPeakDots}
            </svg>
          </div>
        ` : ""}
      </div>
      <div class="tool-section" style="margin-top:10px;">
        <div class="row" style="justify-content:space-between; align-items:center; gap:8px;">
          <div class="small">Selected Record</div>
          <div class="small">${esc(current ? current.measurement_id : "None selected")}</div>
        </div>
        ${current ? `
          <div class="row" style="margin-top:8px; gap:8px;">
            <label class="small" style="flex:1;">Marina ID
              <div class="row" style="gap:8px; align-items:center;">
                <select id="slip-marina-id" style="flex:1;">
                  <option value="">Select marina</option>
                  ${currentOptions.map((entry) => `<option value="${esc(entry.marinaId)}"${entry.marinaId === current.marina_id ? " selected" : ""}>${esc(entry.marinaId)}</option>`).join("")}
                </select>
                <button id="slip-marina-zoom" type="button" class="btn"${currentMarina && Number.isFinite(currentMarina.lat) && Number.isFinite(currentMarina.lng) ? "" : " disabled"}>Zoom To</button>
              </div>
            </label>
            <label class="small" style="flex:1;">Marina Name
              <input id="slip-marina-name" type="text" value="${esc(currentMarina?.marinaName || current.marina_name)}" readonly />
            </label>
          </div>
          <div class="row" style="margin-top:8px; gap:8px;">
            <label class="small" style="flex:1;">Slip ID
              <input id="slip-slip-id" type="text" value="${esc(current.slip_id)}" />
            </label>
            <label class="small" style="flex:1;">Vendor Ref
              <input id="slip-vendor-ref" type="text" value="${esc(current.vendor_slip_ref)}" />
            </label>
          </div>
          <div class="row" style="margin-top:8px; gap:8px;">
            <label class="small" style="flex:1;">Linear Feet
              <input id="slip-linear-feet" type="number" step="0.01" value="${esc(current.measured_linear_feet ?? "")}" />
            </label>
            <label class="small" style="flex:1;">Width Feet
              <input id="slip-width-feet" type="number" step="0.01" value="${esc(current.measured_width_feet ?? "")}" />
            </label>
            <label class="small" style="flex:1;">Depth Feet
              <input id="slip-depth-feet" type="number" step="0.01" value="${esc(current.measured_depth_feet ?? "")}" />
            </label>
          </div>
          <div class="row" style="margin-top:8px; gap:8px;">
            <label class="small">Measurement Date
              <input id="slip-measurement-date" type="date" value="${esc(current.measurement_date)}" />
            </label>
            <label class="small">Confidence
              <select id="slip-confidence">
                <option value="High"${current.confidence_score === "High" ? " selected" : ""}>High</option>
                <option value="Medium"${current.confidence_score === "Medium" ? " selected" : ""}>Medium</option>
                <option value="Low"${current.confidence_score === "Low" ? " selected" : ""}>Low</option>
              </select>
            </label>
          </div>
          <div class="small" style="margin-top:8px;">Geometry WKT</div>
          <textarea readonly style="min-height:64px;">${esc(current.geometry_wkt || "")}</textarea>
          <div class="small" style="margin-top:8px;">Record Created Timestamp</div>
          <input type="text" readonly value="${esc(current.record_created_timestamp || "")}" />
          <div class="row" style="margin-top:10px; gap:8px;">
            <button id="slip-save" type="button" class="btn primary">Save Record</button>
            <button id="slip-delete" type="button" class="btn">Delete Record</button>
            <button id="slip-show-selected" type="button" class="btn">Show on Map</button>
          </div>
        ` : `<div class="small" style="margin-top:8px;">Pick a record from the bottom table or click an inventory point on the map.</div>`}
      </div>
      <div class="small" style="margin-top:10px; color:${status ? "#cbd5e1" : "#94a3b8"};">${esc(status || "Ready.")}</div>
    `;
  }

  function onClick(event) {
    if (event.target?.closest?.("#slip-default-marina-zoom")) {
      fitMarinaById(state.marinaId, state.marinaName);
      return;
    }
    if (event.target?.closest?.("#slip-marina-zoom")) {
      const current = selectedRecord();
      if (!current) return;
      fitMarinaById(String(containerEl?.querySelector("#slip-marina-id")?.value || current.marina_id), current.marina_name);
      return;
    }
    if (event.target?.closest?.("#slip-drop-point")) {
      startPointDrop();
      return;
    }
    if (event.target?.closest?.("#slip-stop-drop")) {
      stopPointDrop("cancelled");
      return;
    }
    if (event.target?.closest?.("#slip-line-layout")) {
      startLineLayout("endpoint");
      return;
    }
    if (event.target?.closest?.("#slip-spacing-run-layout")) {
      startLineLayout("spacing");
      return;
    }
    if (event.target?.closest?.("#slip-line-clear")) {
      lineEndpoints = [];
      lineHoverPoint = null;
      lineLayoutMode = "endpoint";
      imageryEstimateStatus = "";
      imageryEstimateDebug = null;
      stopLineLayout("cleared");
      status = "Line preview cleared.";
      render();
      return;
    }
    if (event.target?.closest?.("#slip-line-estimate-imagery")) {
      if (lineLayoutMode === "spacing") return;
      estimateLineSlipCountFromImagery().catch((error) => {
        imageryEstimateStatus = error?.message || "Imagery estimate failed.";
        app.ui.toast(imageryEstimateStatus);
        render();
      });
      return;
    }
    if (event.target?.closest?.("#slip-line-generate")) {
      const canGenerate = lineLayoutMode === "spacing" ? lineEndpoints.length >= 3 : lineEndpoints.length >= 2;
      if (!canGenerate) return;
      const points = linePreviewPoints();
      createRecordsFromPoints(points).then(() => {
        imageryEstimateStatus = "";
        imageryEstimateDebug = null;
        stopLineLayout("completed");
        app.ui.toast(`Generated ${points.length} slips from line preview.`);
      }).catch((error) => {
        status = error?.message || "Failed to generate line slips.";
        app.ui.toast(status);
        render();
      });
      return;
    }
    if (event.target?.closest?.("#slip-refresh")) {
      refreshRecords().catch(() => {});
      return;
    }
    if (event.target?.closest?.("#slip-export-csv")) {
      exportCsv();
      return;
    }
    if (event.target?.closest?.("#slip-export-json")) {
      exportJson();
      return;
    }
    if (event.target?.closest?.("#slip-save")) {
      saveSelectedRecord().catch((error) => {
        status = error?.message || "Failed to save record.";
        render();
      });
      return;
    }
    if (event.target?.closest?.("#slip-delete")) {
      deleteSelectedRecord().catch((error) => {
        status = error?.message || "Failed to delete record.";
        render();
      });
      return;
    }
    if (event.target?.closest?.("#slip-show-selected")) {
      fitSelection();
    }
  }

  function onInput(event) {
    const id = String(event.target?.id || "");
    if (id === "slip-default-marina-id") {
      setMarinaDefaultsFromId(String(event.target?.value || ""));
      render();
      return;
    }
    if (id === "slip-marina-id") {
      const current = selectedRecord();
      if (!current) return;
      const marina = marinaOptionForId(String(event.target?.value || ""), current.marina_name);
      const nameInput = containerEl?.querySelector("#slip-marina-name");
      if (nameInput) nameInput.value = marina?.marinaName || "";
      return;
    }
    if (id === "slip-default-measurement-date") state.measurementDate = String(event.target?.value || "");
    if (id === "slip-default-confidence") state.confidenceScore = String(event.target?.value || "High");
    if (id === "slip-default-slipid-part1") state.slipIdPart1 = String(event.target?.value || "");
    if (id === "slip-default-slipid-part2") state.slipIdPart2 = String(event.target?.value || "");
    if (id === "slip-default-slipid-part3") state.slipIdPart3 = String(event.target?.value || "");
    if (id === "slip-default-slipid-autoinc") state.slipIdAutoIncrement = !!event.target?.checked;
    if (id === "slip-default-slipid-step") state.slipIdIncrementBy = Math.max(1, Number(event.target?.value || 1) || 1);
    if (id === "slip-default-line-count") {
      state.lineSlipCount = Math.max(2, Number(event.target?.value || 2) || 2);
    }
    if (id.startsWith("slip-default-slipid-")) {
      persist();
      render();
      return;
    }
    if (id === "slip-default-line-count") {
      persist();
      renderPreview();
      render();
      return;
    }
    if (id) persist();
  }

  function bindBottomPanelControls() {
    const host = document.getElementById("slip-inventory-bottom");
    if (!host) return;
    host.querySelector("#slip-bottom-refresh")?.addEventListener("click", () => {
      refreshRecords().catch(() => {});
    });
    host.querySelector("#slip-bottom-show-selected")?.addEventListener("click", () => {
      fitSelection();
    });
    host.querySelectorAll("[data-slip-row]").forEach((row) => {
      row.addEventListener("click", () => {
        state.selectedMeasurementId = String(row.getAttribute("data-slip-row") || "");
        persist();
        render();
      });
    });
  }

  function mount(el) {
    if (containerEl) {
      containerEl.removeEventListener("click", onClick);
      containerEl.removeEventListener("input", onInput);
    }
    containerEl = el;
    containerEl.addEventListener("click", onClick);
    containerEl.addEventListener("input", onInput);
    render();
    refreshRecords({ quiet: true }).catch((error) => {
      status = error?.message || "Slip inventory API is unavailable.";
      render();
    });
  }

  function destroy() {
    stopPointDrop("destroyed");
    stopLineLayout("destroyed");
    try { root.remove(); } catch {}
    app.ui.unmountBottomPanel("slip-inventory");
    if (containerEl) {
      containerEl.removeEventListener("click", onClick);
      containerEl.removeEventListener("input", onInput);
    }
    containerEl = null;
  }

  function snapshot() {
    return { version: 2, state };
  }

  function restore(data) {
    if (!data?.state || typeof data.state !== "object") return;
    state = { ...defaultState(), ...data.state };
    state.measurementDate = todayDate();
    state.lineSlipCount = Math.max(2, Number(state.lineSlipCount || 6) || 6);
    persist();
    render();
  }

  function setMarinaItems(items) {
    marinaCatalog = normalizeMarinaCatalog(items);
    if (state.marinaId) {
      setMarinaDefaultsFromId(state.marinaId);
    }
    if (containerEl) render();
  }

  return { mount, destroy, snapshot, restore, setMarinaItems };
}

export const slipDeliveryTool = {
  id: "slip-delivery",
  name: "Slip Inventory",
  init(app) { this._rt = this._rt ?? createSlipInventoryRuntime(app); },
  destroy() { this._rt?.destroy?.(); this._rt = null; },
  renderPanel(app, el) {
    if (!this._rt) this._rt = createSlipInventoryRuntime(app);
    this._rt.mount(el);
  },
  serialize() { return this._rt?.snapshot?.() ?? null; },
  restore(app, data) {
    if (!this._rt) this._rt = createSlipInventoryRuntime(app);
    this._rt.restore?.(data);
  },
};
